import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LogOut, AlertOctagon, CreditCard, Building2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import FacturacionTab from './FacturacionTab';

/**
 * Pantalla dedicada de "Pagar para continuar" que se muestra cuando la empresa
 * ha superado el periodo de prueba + días de gracia sin suscripción activa.
 * Sólo permite al admin acceder a Facturación o cerrar sesión.
 */
export default function BloqueoPagoScreen() {
  const { user, logout } = useAuth();
  const [pagar, setPagar] = useState(false);

  const salir = () => { logout(); window.location.href = '/'; };

  // Colaboradores/gestoría/auditor no pueden pagar, sólo el admin principal
  const esAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="bloqueo-pago-screen">
      <header className="bg-white border-b border-zinc-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-red-600"><AlertOctagon className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="font-heading text-lg font-bold text-zinc-900">{user?.empresa_nombre || 'Tu empresa'}</h1>
              <p className="text-zinc-500 text-xs">Cuenta bloqueada por falta de pago</p>
            </div>
          </div>
          <Button variant="outline" onClick={salir} data-testid="bloqueo-salir">
            <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
          </Button>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-4xl">
        {!pagar ? (
          <div className="space-y-6">
            <Card className="border-red-200 bg-red-50/30">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="shrink-0 p-3 rounded-xl bg-red-100">
                    <AlertOctagon className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading text-2xl text-red-900 mb-1">
                      Tu cuenta está bloqueada
                    </CardTitle>
                    <p className="text-sm text-red-800 leading-relaxed">
                      El periodo de prueba y los días de gracia han finalizado sin recibir un pago.
                      Para seguir usando la plataforma y recuperar el acceso a todos tus datos,
                      elige un plan y realiza el pago desde este panel.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {esAdmin ? (
                  <Button onClick={() => setPagar(true)} className="bg-zinc-900 hover:bg-zinc-800 h-11 px-6" data-testid="bloqueo-ir-a-pagar">
                    <CreditCard className="h-4 w-4 mr-2" /> Elegir plan y pagar
                  </Button>
                ) : (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                    Sólo el <b>administrador principal</b> puede gestionar el pago. Pide al admin que active la cuenta.
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardContent className="pt-6 text-center">
                  <CreditCard className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
                  <h3 className="font-heading font-semibold text-sm mb-1">Tarjeta</h3>
                  <p className="text-xs text-zinc-500">Pago instantáneo con Stripe. Renovación automática.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Building2 className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
                  <h3 className="font-heading font-semibold text-sm mb-1">Transferencia</h3>
                  <p className="text-xs text-zinc-500">Recibirás los datos bancarios. Activamos en 1-2 días laborables.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
                  <h3 className="font-heading font-semibold text-sm mb-1">Domiciliación SEPA</h3>
                  <p className="text-xs text-zinc-500">Firma electrónica del mandato y cobros automáticos.</p>
                </CardContent>
              </Card>
            </div>

            <div className="rounded-lg bg-zinc-100 border border-zinc-200 p-4 text-xs text-zinc-600 leading-relaxed">
              <b>Tus datos están a salvo.</b> Fichajes, empleados y configuración se mantienen intactos.
              En cuanto confirmemos el pago, la cuenta se reactivará automáticamente y podrás seguir usando la
              plataforma con normalidad. Si necesitas ayuda, contacta con soporte.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Button variant="outline" onClick={() => setPagar(false)}>← Volver</Button>
            <FacturacionTab />
          </div>
        )}
      </main>
    </div>
  );
}
